
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  Link,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  AlertCircle,
  Loader2,
  Save,
} from 'lucide-react';

import { useSchool } from '../../../context/SchoolContext';
import { routeConstants } from '../../../constants/routeConstants';
import { rbacSettingsApi } from '../../settings/services/rbacSettingsApi';
import StaffBasicDetails from '../components/StaffBasicDetails';
import StaffContactDetails from '../components/StaffContactDetails';
import StaffDocuments from '../components/StaffDocuments';
import StaffEmploymentDetails from '../components/StaffEmploymentDetails';
import StaffLoginAccess from '../components/StaffLoginAccess';
import Toast from '../components/Toast';
import { staffApi } from '../services/staffApi';
import {
  getStaffApiErrorMessage,
  getStaffApiFieldErrors,
} from '../utils/staffErrors';

const STAFF_LIST_ROUTE = routeConstants.STAFF || '/staff';

const createInitialFormData = () => ({
  firstName: '',
  middleName: '',
  lastName: '',
  gender: '',
  dob: '',
  phone: '',
  email: '',
  address: '',
  photo: null,
  photoUrl: '',

  employeeId: '',
  staffType: '',
  department: '',
  designation: '',
  qualification: '',
  joiningDate: '',

  alternatePhone: '',

  enableLogin: false,
  selectedRoleIds: [],

  status: 'ACTIVE',
  documents: [],
  existingDocuments: [],
});

const appendValue = (payload, key, value) => {
  if (
    value !== undefined &&
    value !== null &&
    value !== ''
  ) {
    payload.append(key, value);
  }
};

const validPhone = (value) => {
  if (!value.trim()) return true;
  if (!/^[0-9+\-\s()]+$/.test(value.trim())) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
};

const validEmail = (value) =>
  !value.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const AddStaffPage = () => {
  const { schoolId } = useSchool();
  const navigate = useNavigate();
  const { id: staffId } = useParams();

  const isEditMode = Boolean(staffId);

  const [formData, setFormData] = useState(createInitialFormData);
  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [originalLoginEnabled, setOriginalLoginEnabled] = useState(false);
  const [linkedUserId, setLinkedUserId] = useState(null);
  const [userVerified, setUserVerified] = useState(false);

  const loadRoles = useCallback(async () => {
    if (!schoolId) return [];

    setRolesLoading(true);
    try {
      const roleList = await rbacSettingsApi.listRoles(schoolId);
      const activeRoles = (roleList || []).filter(
        (role) => !role.status || role.status === 'ACTIVE',
      );
      setRoles(activeRoles);
      return activeRoles;
    } catch (error) {
      setToast({
        type: 'error',
        message: getStaffApiErrorMessage(error, 'Unable to load roles.'),
      });
      return [];
    } finally {
      setRolesLoading(false);
    }
  }, [schoolId]);

  const loadStaffForEdit = useCallback(async () => {
    if (!schoolId || !staffId) return;

    setPageLoading(true);
    try {
      const response = await staffApi.getStaffById(schoolId, staffId);
      const staff = response?.data ?? response;
      const loginEnabled =
        staff?.user_is_active === true ||
        staff?.login_status === 'ENABLED';

      let selectedRoleIds = [];
      if (staff?.user_id) {
        const assignedRoles = await rbacSettingsApi.listUserRoles(
          schoolId,
          staff.user_id,
        );
        selectedRoleIds = (assignedRoles || [])
          .map((role) => role.id || role.role_id)
          .filter(Boolean)
          .map(String);
      }

      setLinkedUserId(staff?.user_id || null);
      setUserVerified(Boolean(staff?.user_is_verified));
      setOriginalLoginEnabled(loginEnabled);

      setFormData({
        firstName: staff?.first_name || '',
        middleName: staff?.middle_name || '',
        lastName: staff?.last_name || '',
        gender: staff?.gender || '',
        dob: staff?.date_of_birth || '',
        phone: staff?.phone || '',
        email: staff?.email || '',
        address: staff?.address || '',
        photo: null,
        photoUrl: staff?.photo || '',

        employeeId: staff?.employee_id || '',
        staffType: staff?.staff_type || '',
        department: staff?.department || '',
        designation: staff?.designation || '',
        qualification: staff?.qualification || '',
        joiningDate: staff?.joining_date || '',

        alternatePhone: staff?.alternate_phone || '',

        enableLogin: loginEnabled,
        selectedRoleIds,

        status: staff?.status || 'ACTIVE',
        documents: [],
        existingDocuments: staff?.documents || [],
      });
    } catch (error) {
      setToast({
        type: 'error',
        message: getStaffApiErrorMessage(
          error,
          'Unable to load staff details.',
        ),
      });
    } finally {
      setPageLoading(false);
    }
  }, [schoolId, staffId]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    if (isEditMode) loadStaffForEdit();
  }, [isEditMode, loadStaffForEdit]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));

    setErrors((current) => ({
      ...current,
      [name]: '',
    }));
  };

  const handleToggleRole = (roleId) => {
    const normalizedRoleId = String(roleId);

    setFormData((current) => {
      const selected = current.selectedRoleIds.map(String);
      return {
        ...current,
        selectedRoleIds: selected.includes(normalizedRoleId)
          ? selected.filter((id) => id !== normalizedRoleId)
          : [...selected, normalizedRoleId],
      };
    });

    setErrors((current) => ({
      ...current,
      selectedRoleIds: '',
    }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) {
      nextErrors.firstName = 'First name is required.';
    }
    if (!formData.gender) {
      nextErrors.gender = 'Gender is required.';
    }
    if (!formData.dob) {
      nextErrors.dob = 'Date of birth is required.';
    } else if (new Date(`${formData.dob}T00:00:00`) > new Date()) {
      nextErrors.dob = 'Date of birth cannot be in the future.';
    }
    if (!formData.staffType) {
      nextErrors.staffType = 'Staff type is required.';
    }
    if (!formData.designation.trim()) {
      nextErrors.designation = 'Designation is required.';
    }
    if (!formData.department.trim()) {
      nextErrors.department = 'Department is required.';
    }
    if (!formData.joiningDate) {
      nextErrors.joiningDate = 'Joining date is required.';
    }
    if (!formData.status) {
      nextErrors.status = 'Status is required.';
    }
    if (!validPhone(formData.phone)) {
      nextErrors.phone = 'Phone must contain between 10 and 15 digits.';
    }
    if (!validPhone(formData.alternatePhone)) {
      nextErrors.alternatePhone =
        'Alternate phone must contain between 10 and 15 digits.';
    }
    if (!validEmail(formData.email)) {
      nextErrors.email = 'Enter a valid email address.';
    }
    if (formData.enableLogin && !formData.email.trim()) {
      nextErrors.email = 'Email is required when login access is enabled.';
    }
    if (
      formData.enableLogin &&
      formData.selectedRoleIds.length === 0
    ) {
      nextErrors.selectedRoleIds =
        'Select at least one role before enabling login.';
    }

    setErrors(nextErrors);
    return nextErrors;
  };

  const buildStaffPayload = ({ includeLoginField = false } = {}) => {
    const payload = new FormData();

    appendValue(payload, 'first_name', formData.firstName.trim());
    appendValue(payload, 'middle_name', formData.middleName.trim());
    appendValue(payload, 'last_name', formData.lastName.trim());
    appendValue(payload, 'gender', formData.gender);
    appendValue(payload, 'date_of_birth', formData.dob);
    appendValue(payload, 'phone', formData.phone.trim());
    appendValue(payload, 'alternate_phone', formData.alternatePhone.trim());
    appendValue(payload, 'email', formData.email.trim().toLowerCase());
    appendValue(payload, 'address', formData.address.trim());
    appendValue(payload, 'staff_type', formData.staffType);
    appendValue(payload, 'designation', formData.designation.trim());
    appendValue(payload, 'department', formData.department.trim());
    appendValue(payload, 'qualification', formData.qualification.trim());
    appendValue(payload, 'joining_date', formData.joiningDate);
    appendValue(payload, 'status', formData.status);

    if (
      typeof File !== 'undefined' &&
      formData.photo instanceof File
    ) {
      payload.append('photo', formData.photo);
    }

    if (includeLoginField) {
      payload.append('enable_login', String(Boolean(formData.enableLogin)));
    }

    return payload;
  };

  const synchronizeUserRoles = async (userId) => {
    if (!userId) {
      throw new Error('The linked user ID was not returned.');
    }

    const currentRoles = await rbacSettingsApi.listUserRoles(
      schoolId,
      userId,
    );

    const currentRoleIds = new Set(
      (currentRoles || [])
        .map((role) => String(role.id || role.role_id || ''))
        .filter(Boolean),
    );

    const desiredRoleIds = new Set(
      (formData.selectedRoleIds || []).map(String).filter(Boolean),
    );

    const rolesToAdd = [...desiredRoleIds].filter(
      (roleId) => !currentRoleIds.has(roleId),
    );
    const rolesToRemove = [...currentRoleIds].filter(
      (roleId) => !desiredRoleIds.has(roleId),
    );

    for (const roleId of rolesToAdd) {
      await rbacSettingsApi.assignUserRole(schoolId, userId, roleId);
    }

    for (const roleId of rolesToRemove) {
      await rbacSettingsApi.removeUserRole(schoolId, userId, roleId);
    }
  };

  const uploadSelectedDocuments = async (targetStaffId) => {
    const files = (Array.isArray(formData.documents)
      ? formData.documents
      : []
    ).filter(
      (item) =>
        typeof File !== 'undefined' &&
        item instanceof File,
    );

    const failures = [];

    for (const file of files) {
      const uploadData = new FormData();
      uploadData.append('file', file, file.name);
      uploadData.append('document_type', 'STAFF_DOCUMENT');
      uploadData.append('document_number', '');

      try {
        await staffApi.uploadStaffDocument(
          schoolId,
          targetStaffId,
          uploadData,
        );
      } catch (error) {
        failures.push({
          fileName: file.name,
          error,
        });
      }
    }

    return failures;
  };

  const handleCreate = async () => {
    const response = await staffApi.createStaff(
      schoolId,
      buildStaffPayload({ includeLoginField: true }),
    );

    let createdStaff = response?.data ?? response;
    if (!createdStaff?.id) {
      throw new Error('Staff was created but the staff ID was not returned.');
    }

    if (formData.enableLogin) {
      if (!createdStaff.user_id) {
        const detailResponse = await staffApi.getStaffById(
          schoolId,
          createdStaff.id,
        );
        createdStaff = detailResponse?.data ?? detailResponse;
      }

      await synchronizeUserRoles(createdStaff.user_id);
    }

    const documentFailures = await uploadSelectedDocuments(
      createdStaff.id,
    );

    return {
      message: response?.message || 'Staff created successfully.',
      staffId: createdStaff.id,
      documentFailures,
    };
  };

  const handleUpdate = async () => {
    await staffApi.updateStaff(
      schoolId,
      staffId,
      buildStaffPayload(),
    );

    let currentUserId = linkedUserId;
    const loginStateChanged =
      originalLoginEnabled !== formData.enableLogin;

    if (loginStateChanged) {
      const toggleResponse = await staffApi.toggleLoginAccess(
        schoolId,
        staffId,
        formData.enableLogin,
        {
          roleIds: formData.enableLogin
            ? formData.selectedRoleIds
            : undefined,
        },
      );

      const toggledStaff = toggleResponse?.data ?? toggleResponse;
      currentUserId = toggledStaff?.user_id || currentUserId;
    }

    if (formData.enableLogin) {
      if (!currentUserId) {
        const detailResponse = await staffApi.getStaffById(
          schoolId,
          staffId,
        );
        currentUserId = (detailResponse?.data ?? detailResponse)?.user_id;
      }
      await synchronizeUserRoles(currentUserId);
    }

    const documentFailures = await uploadSelectedDocuments(staffId);

    return {
      message: formData.enableLogin
        ? 'Staff details, login access, and roles updated successfully.'
        : 'Staff details updated successfully.',
      staffId,
      documentFailures,
    };
  };

  const handleSubmit = async () => {
    if (!schoolId || saving) return;

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setToast({
        type: 'error',
        message: Object.values(validationErrors)[0],
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    setToast(null);

    try {
      const result = isEditMode
        ? await handleUpdate()
        : await handleCreate();

      if (result.documentFailures.length > 0) {
        const failedNames = result.documentFailures
          .map((item) => item.fileName)
          .join(', ');

        setToast({
          type: 'error',
          message: `${result.message} Document upload failed for: ${failedNames}.`,
        });

        window.setTimeout(
          () => navigate(`/staff/view/${result.staffId}`),
          2400,
        );
        return;
      }

      setToast({ type: 'success', message: result.message });
      window.setTimeout(
        () => navigate(`/staff/view/${result.staffId}`),
        1400,
      );
    } catch (error) {
      const backendErrors = getStaffApiFieldErrors(error);
      const mappedErrors = {};

      const fieldMap = {
        first_name: 'firstName',
        middle_name: 'middleName',
        last_name: 'lastName',
        date_of_birth: 'dob',
        phone: 'phone',
        alternate_phone: 'alternatePhone',
        email: 'email',
        staff_type: 'staffType',
        designation: 'designation',
        department: 'department',
        qualification: 'qualification',
        joining_date: 'joiningDate',
        status: 'status',
      };

      Object.entries(backendErrors).forEach(([path, message]) => {
        mappedErrors[fieldMap[path] || path] = message;
      });

      setErrors((current) => ({ ...current, ...mappedErrors }));
      setToast({
        type: 'error',
        message: getStaffApiErrorMessage(
          error,
          isEditMode
            ? 'Unable to update staff.'
            : 'Unable to create staff.',
        ),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <Loader2 className="h-9 w-9 animate-spin text-blue-600" />
        <p className="mt-3 text-sm text-gray-500">
          Loading staff details...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div>
        <div className="mb-2 text-sm text-gray-500">
          <Link to={STAFF_LIST_ROUTE} className="hover:text-blue-600">
            Staff Management
          </Link>
          <span> / </span>
          <span>{isEditMode ? 'Edit Staff' : 'Add Staff'}</span>
        </div>

        <h1 className="text-3xl font-bold text-slate-900">
          {isEditMode ? 'Edit Staff / Teacher' : 'Add Staff / Teacher'}
        </h1>
        <p className="mt-2 text-gray-500">
          Fields marked with <span className="text-red-500">*</span> are mandatory.
          Optional fields are labelled clearly. Locked fields are shown in gray.
        </p>
      </div>

      {Object.keys(errors).length > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Please correct the form.</p>
            <p className="mt-1">{Object.values(errors).find(Boolean)}</p>
          </div>
        </div>
      )}

      <StaffBasicDetails
        formData={formData}
        setFormData={setFormData}
        handleChange={handleChange}
        errors={errors}
        emailLocked={Boolean(isEditMode && linkedUserId)}
      />

      <StaffEmploymentDetails
        formData={formData}
        handleChange={handleChange}
        errors={errors}
      />

      <StaffContactDetails
        formData={formData}
        handleChange={handleChange}
        errors={errors}
      />

      <StaffLoginAccess
        formData={formData}
        handleChange={handleChange}
        roles={roles}
        rolesLoading={rolesLoading}
        onToggleRole={handleToggleRole}
        isEditMode={isEditMode}
        originalLoginEnabled={originalLoginEnabled}
        userId={linkedUserId}
        userVerified={userVerified}
      />

      {errors.selectedRoleIds && (
        <p className="-mt-4 text-sm font-medium text-red-600">
          {errors.selectedRoleIds}
        </p>
      )}

      <StaffDocuments
        formData={formData}
        setFormData={setFormData}
      />

      <div className="flex justify-end gap-3 pb-8">
        <button
          type="button"
          onClick={() => navigate(STAFF_LIST_ROUTE)}
          disabled={saving}
          className="rounded-xl border border-gray-300 px-6 py-3 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving || rolesLoading}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Save className="h-5 w-5" />
          )}
          {saving
            ? 'Saving...'
            : isEditMode
              ? 'Update Staff'
              : 'Save Staff'}
        </button>
      </div>
    </div>
  );
};

export default AddStaffPage;
